import type { SelectOption } from '@/components/ui';
import { COUNTRIES, type Country } from './countryCodes';

/**
 * E 국적 — 국적 선택/필터 드롭다운 옵션 빌더.
 *
 * 회원 일괄발송 세그먼트(NotificationSendBulkPage)·주문 엑셀 export(OrderExportDialog) 등
 * BO 의 국적 드롭다운이 공유한다. value=ISO alpha-2 코드, label=현재 로케일 표시명.
 * 정본은 {@link COUNTRIES}(be-core country-codes.json 동기화, 주요국 우선 정렬).
 */

type LocaleNameKey = 'nameKo' | 'nameEn' | 'nameJa' | 'nameZh';

/** i18next language(예: 'ko-KR') → Country 표시명 필드. 미지원 로케일은 영문. */
const nameKeyForLocale = (locale: string | undefined): LocaleNameKey => {
  const lang = (locale ?? '').toLowerCase();
  if (lang.startsWith('ko')) return 'nameKo';
  if (lang.startsWith('ja')) return 'nameJa';
  if (lang.startsWith('zh')) return 'nameZh';
  return 'nameEn';
};

export const countryLabel = (country: Country, locale: string | undefined): string =>
  country[nameKeyForLocale(locale)];

export interface NationalityOptionsConfig {
  /** 선두에 넣을 "전체/미지정" 옵션. 지정 시 value='' 로 추가된다. */
  allLabel?: string;
}

/**
 * 국적 드롭다운 옵션(주요국 우선 정렬). 로케일별 표시명으로 라벨링한다.
 *
 * @param locale i18next language (예: 'ko-KR'). undefined 면 영문.
 * @param config allLabel 지정 시 선두에 value='' 옵션 추가.
 */
export const nationalityOptions = (
  locale: string | undefined,
  config: NationalityOptionsConfig = {},
): SelectOption[] => {
  const options: SelectOption[] = COUNTRIES.map((c) => ({
    value: c.code,
    label: countryLabel(c, locale),
  }));
  if (config.allLabel !== undefined) {
    return [{ value: '', label: config.allLabel }, ...options];
  }
  return options;
};
